/**
 * VerticalLiveFeed — TikTok-style vertical scroll-snap feed of live streams.
 *
 * Each slide is a full-viewport card with the stream's cover image, host info,
 * viewer count, and a big "Join live" CTA. `IntersectionObserver` tracks the
 * currently-visible slide so it can be pre-highlighted (and later, pre-warmed
 * for instant join). Tapping the CTA opens the parent's LiveRoom overlay.
 */
import { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Eye, Radio, ChevronUp } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import useLivePreview from "@/hooks/useLivePreview";
import VerifiedBadge from "@/components/VerifiedBadge";

function LiveSlide({ stream, isActive, onJoin }) {
  const { ref: previewRef, src, onError } = useLivePreview(stream);
  return (
    <div
      ref={previewRef}
      data-slide-id={stream.id}
      data-testid={`live-slide-${stream.id}`}
      className="snap-start h-full w-full relative flex items-end justify-center bg-black overflow-hidden"
    >
      {/* Cover image — live preview frame when available, ken-burns pan */}
      {src ? (
        <img
          src={src}
          onError={onError}
          alt={stream.title || "Untitled Live"}
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-linear ${
            isActive ? "scale-110" : "scale-100"
          }`}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900/50 via-black to-black" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />

      {/* Top-left LIVE pill + viewer chip */}
      <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" /> Live
        </div>
        <div className="flex items-center gap-1 px-2.5 py-1 bg-black/60 backdrop-blur text-white text-xs rounded-full">
          <Eye size={12} /> {(stream.viewer_count || 0).toLocaleString()}
        </div>
      </div>

      {/* Bottom content: host + title + Join button */}
      <div className="relative z-10 p-6 pb-24 w-full max-w-lg mx-auto text-white space-y-4">
        <UserBadge
          userRef={stream.host?.id || stream.host?.handle}
          href={`/u/${stream.host?.id}`}
          className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md"
          data-testid={`vlf-host-${stream.id}`}
        >
          <Avatar className={`w-12 h-12 border border-white/20`}>
            <AvatarImage src={stream.host?.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white">
              {stream.host?.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className={`text-base font-semibold truncate flex items-center gap-1.5`}>
              <span className="truncate">{stream.host?.display_name}</span>
              <VerifiedBadge show={stream.host?.is_verified} size="sm" />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {stream.category && (
                <span className="text-[10px] uppercase tracking-widest bg-white/10 backdrop-blur border border-white/15 rounded-full px-2 py-0.5 text-white/90">
                  {stream.category}
                </span>
              )}
            </div>
          </div>
        </UserBadge>

        <div className="font-display text-2xl sm:text-3xl leading-tight drop-shadow line-clamp-3">
          {stream.title || "Untitled Live"}
        </div>

        <button
          type="button"
          onClick={() => onJoin(stream)}
          data-testid={`live-slide-join-${stream.id}`}
          className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold text-base rounded-full py-3.5 transition-colors shadow-[0_10px_32px_rgba(225,29,72,0.4)] active:scale-[0.98]"
        >
          <Radio size={16} /> Join live
        </button>
      </div>

      {/* Swipe-up affordance (dims once the user has scrolled past the first slide) */}
      {isActive && (
        <div className="absolute bottom-24 right-4 z-10 flex flex-col items-center text-white/70 text-[10px] uppercase tracking-widest pointer-events-none animate-bounce">
          <ChevronUp size={16} />
          Swipe
        </div>
      )}
    </div>
  );
}

export default function VerticalLiveFeed({ streams, onJoin, onClose }) {
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(streams[0]?.id || null);

  // Track which slide is currently in view.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const id = e.target.getAttribute("data-slide-id");
            if (id) setActiveId(id);
          }
        }
      },
      { root, threshold: [0.6] }
    );
    root.querySelectorAll("[data-slide-id]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [streams.length]);

  return (
    <div className="fixed inset-0 z-[70] bg-black" data-testid="vertical-live-feed">
      {/* Top-right close */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="vertical-feed-close"
          className="absolute top-4 right-4 z-30 h-9 w-9 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white grid place-items-center hover:bg-white/10 transition-colors"
        >
          ✕
        </button>
      )}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {streams.map((s) => (
          <div key={s.id} className="h-screen w-full">
            <LiveSlide stream={s} isActive={s.id === activeId} onJoin={onJoin} />
          </div>
        ))}
      </div>
    </div>
  );
}
